import React from 'react';
import axios from 'axios';
import { X, AlertTriangle, Cloud, Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Manual conflict-resolution modal — surfaces after an offline sync when the
 * server-side copy of one or more loads has been edited by dispatch while the
 * driver was offline. All server changes have already been applied locally
 * (drainQueue refreshes the IDB cache), so this modal is an AWARENESS surface
 * rather than a two-way merge: it shows the driver exactly which fields
 * dispatch changed, letting them acknowledge before proceeding.
 *
 * Props
 *   conflicts   Array of { load_id, load_ref, changes[], ... }
 *   onClose     Called when the driver clicks Acknowledge / dismisses.
 */
const _fmt = (v) => {
  if (v === null || v === undefined) return <span className="text-slate-400 italic">empty</span>;
  if (typeof v === 'number') return String(v);
  const s = String(v);
  return s.length > 80 ? s.slice(0, 77) + '…' : s;
};

const ConflictRow = ({ change }) => (
  <div
    data-testid={`conflict-row-${change.key}`}
    className="grid grid-cols-[minmax(0,7rem)_1fr_1fr] gap-3 py-2 border-b border-slate-100 last:border-b-0 text-xs"
  >
    <div className="font-semibold text-slate-700 self-center">{change.label}</div>
    <div className="flex items-start gap-1.5 min-w-0">
      <Smartphone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Your view</div>
        <div className="text-slate-700 break-words">{_fmt(change.client)}</div>
      </div>
    </div>
    <div className="flex items-start gap-1.5 min-w-0">
      <Cloud className="w-3.5 h-3.5 text-[#002FA7] flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#002FA7]">Dispatch update</div>
        <div className="text-slate-900 font-medium break-words">{_fmt(change.server)}</div>
      </div>
    </div>
  </div>
);

const ConflictCard = ({ conflict }) => (
  <div
    data-testid={`conflict-card-${conflict.load_id}`}
    className="rounded-md border border-amber-200 bg-white overflow-hidden"
  >
    <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
      <div className="text-sm font-semibold text-amber-900">
        Load <span className="font-mono">{conflict.load_ref || conflict.load_id.slice(-6)}</span>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-amber-700">
        {(conflict.changes || []).length} change{(conflict.changes || []).length === 1 ? '' : 's'}
      </div>
    </div>
    <div className="px-4 py-2">
      {(conflict.changes || []).length === 0 ? (
        <div className="py-2 text-xs text-slate-500">
          Dispatch updated this load — no key fields changed, but the record was refreshed.
        </div>
      ) : (
        (conflict.changes || []).map((c) => <ConflictRow key={c.key} change={c} />)
      )}
    </div>
  </div>
);

const ConflictResolutionModal = ({ conflicts, onClose }) => {
  if (!conflicts || conflicts.length === 0) return null;
  // POST an acknowledgement row per conflict so ops has a paper trail of
  // every offline collision. Best-effort — a failed POST won't block the
  // driver from dismissing the modal (the local cache is already
  // authoritative at this point). We fire in parallel so the "Got it"
  // click stays snappy.
  const handleAcknowledge = () => {
    conflicts.forEach((c) => {
      axios
        .post(
          `${API}/conflict-acknowledgements`,
          {
            load_id: c.load_id,
            load_ref: c.load_ref,
            changes: c.changes || [],
            client_updated_at: c.client_updated_at,
            server_updated_at: c.server_updated_at,
          },
          { withCredentials: true },
        )
        .catch(() => {
          /* audit-log write is best-effort — swallow so UX isn't blocked */
        });
    });
    onClose();
  };
  return (
    <div
      data-testid="conflict-modal"
      className="fixed inset-0 z-[60] bg-slate-900/50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="conflict-modal-title"
    >
      <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
        <header className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="conflict-modal-title"
              className="text-base font-bold text-slate-900"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Dispatch updated {conflicts.length} load{conflicts.length === 1 ? '' : 's'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Your work synced successfully. Here&apos;s what changed on dispatch&apos;s side while you were offline.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="conflict-modal-close"
            aria-label="Close"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50">
          {conflicts.map((c) => (
            <ConflictCard key={c.load_id} conflict={c} />
          ))}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500">
            Server values are already applied on this device.
          </div>
          <button
            type="button"
            onClick={handleAcknowledge}
            data-testid="conflict-modal-acknowledge"
            className="px-4 py-2 rounded-md bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001f6b] transition-colors"
          >
            Got it
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;
